import React from 'react';
import { Button, Card, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { BillingMode, SetupPackageType } from '../../services/exports/Constants';
import useHelpers from '../../hooks/context/useHelpers';
import { applyContractDiscount } from '../../services/helpers/ContractDiscountHelpers';

function Price({ amount, active, discount = 0, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SetupPackage' });

  const { formatCurrency } = useHelpers();

  // Only the active (selected billing mode) price carries the annual-contract
  // discount: the original is shown struck via the `Was …` subtext while the
  // headline shows the discounted amount.
  const discounted = active ? applyContractDiscount(amount, discount) : amount;
  const isDiscounted = active && discount > 0 && discounted !== amount;

  if (isDiscounted) {
    return (
      <span className={className}>
        <h2 className="mb-0">{formatCurrency(discounted)}</h2>
        <Flex align="center" gap="small" className="mt-1">
          <p className="text-[12px] mb-0">
            {'/ '}
            {t('tags.net_one_time')}
          </p>
          <span className="rounded border border-solid border-green-200 bg-green-50 px-[6px] py-[1px] text-[11px] font-semibold leading-none text-green-700">
            {t('tags.annual_discount', { percentage: discount })}
          </span>
        </Flex>
        <p className="text-[12px] text-[#707070] mb-0">{t('labels.was', { amount: formatCurrency(amount) })}</p>
      </span>
    );
  }

  return (
    <span className={className}>
      <h5 className={classnames('inline-block', { 'line-through': !active })}>{formatCurrency(amount)}</h5>
      <p className={classnames('inline-block text-[12px] mt-auto', { 'line-through': !active })}>
        {'/ '}
        {t('tags.net_one_time')}
      </p>
    </span>
  );
}

function Feature({ text, color, className }) {
  return (
    <div className={classnames('px-md pt-md mt-md border-t-[1px] border-gray-200 border-0 border-solid', className)}>
      <p style={{ color }} className="font-semibold">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" className="h-5 w-5 my-auto">
          <path
            xmlns="http://www.w3.org/2000/svg"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.6145 1.36104C10.6832 0.574966 9.32075 0.574967 8.38944 1.36104L7.54128 2.07692C7.14544 2.41102 6.6556 2.61394 6.13945 2.65759L5.0335 2.75111C3.81913 2.85381 2.85577 3.81717 2.75307 5.03155L2.65954 6.1375C2.61589 6.65365 2.41298 7.14349 2.07888 7.53934L1.36299 8.38749C0.576919 9.3188 0.57692 10.6812 1.36299 11.6125L2.07888 12.4607C2.41298 12.8565 2.61589 13.3464 2.65954 13.8625L2.75307 14.9685C2.85577 16.1829 3.81913 17.1462 5.0335 17.2489L6.13945 17.3424C6.6556 17.3861 7.14544 17.589 7.54129 17.9231L8.38944 18.639C9.32075 19.425 10.6832 19.425 11.6145 18.639L12.4626 17.9231C12.8585 17.589 13.3483 17.3861 13.8645 17.3424L14.9705 17.2489C16.1848 17.1462 17.1482 16.1829 17.2508 14.9685L17.3443 13.8625C17.3881 13.3464 17.591 12.8565 17.9251 12.4607L18.641 11.6125C19.427 10.6812 19.427 9.3188 18.641 8.38749L17.9251 7.53932C17.591 7.14349 17.3881 6.65365 17.3443 6.1375L17.2508 5.03155C17.1482 3.81717 16.1848 2.85381 14.9705 2.75111L13.8645 2.65759C13.3483 2.61394 12.8585 2.41102 12.4626 2.07692L11.6145 1.36104ZM14.548 8.29555C14.9873 7.85621 14.9873 7.1439 14.548 6.70456C14.1087 6.26521 13.3963 6.26521 12.957 6.70456L8.75249 10.9091L7.04799 9.20456C6.60865 8.76521 5.89634 8.76521 5.457 9.20456C5.01765 9.6439 5.01765 10.3562 5.457 10.7956L7.957 13.2955C8.39634 13.7349 9.10865 13.7349 9.54799 13.2955L14.548 8.29555Z"
            fill={color}
          />
        </svg>
        <span className="my-auto"> {text}</span>
      </p>
    </div>
  );
}

export default function SetupPackage({ data, billingMode, discount = 0, loading, onSelect, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SetupPackage' });

  const featuresColor = {
    [SetupPackageType.Default]: '#252430',
    [SetupPackageType.Preferred]: '#69CE8C',
    [SetupPackageType.Custom]: '#D5AE82',
  }[data.type];

  return (
    <Card
      className={classnames(className, { 'opacity-50': data.type === SetupPackageType.Custom })}
      classNames={{ body: '!px-none !py-md' }}
    >
      <div className="px-md">
        <Flex gap="large">
          <h2 className="my-auto">{data.name}</h2>
          {data.type === SetupPackageType.Preferred && (
            <div className="bg-brand-primary rounded-3xl p-xs my-auto">
              <p className="font-semibold text-md">{t('tags.preferred')}</p>
            </div>
          )}
        </Flex>
        {Boolean(data.short_description) && <h5 className="mt-md text-[#707070]">{data.short_description}</h5>}
        <Flex gap="small" className="mt-md">
          {data.type === SetupPackageType.Custom ? (
            <h5>{t('labels.custom')}</h5>
          ) : (
            <>
              <Price
                amount={data.internal_invoice_billing_price}
                discount={discount}
                active={
                  billingMode === BillingMode.InternalInvoicing ||
                  data.internal_invoice_billing_price === data.stripe_billing_price
                }
              />
              {billingMode === BillingMode.Stripe &&
                data.internal_invoice_billing_price !== data.stripe_billing_price && (
                  <Price
                    amount={data.stripe_billing_price}
                    discount={discount}
                    active={billingMode === BillingMode.Stripe}
                  />
                )}
            </>
          )}
        </Flex>
        <Button
          className="mt-md w-full p-md text-sm font-semibold"
          color="default"
          variant="solid"
          size="large"
          loading={loading}
          onClick={onSelect}
        >
          {t('buttons.select_plan')}
        </Button>
      </div>
      {Boolean(data.features) && (
        <div className="mt-md">
          {data.features.map((item) => (
            <Feature text={item} color={featuresColor} key={item} />
          ))}
        </div>
      )}
    </Card>
  );
}
